import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
} from 'reactstrap';
import { getCategories } from '../../redux/actions/provider';
import { setGetCategories } from '../../redux/actions/provider';

const CategorySelectionModal = ({
  isOpen,
  onClose,
  onConfirm,
  selectedCategories = [],
}) => {
  const dispatch = useDispatch();
  const categoriesState = useSelector((state) => state.provider.categories);
  const [selectedCats, setSelectedCats] = useState(selectedCategories);
  const [searchTerm, setSearchTerm] = useState('');
  const [includeClientsWithoutInterests, setIncludeClientsWithoutInterests] =
    useState(false);

  const clearReduxState = (actionCreator, delay = 3500) => {
    // Clear update state after specified delay
    setTimeout(() => {
      dispatch(actionCreator(null));
    }, delay);
  };

  useEffect(() => {
    if (isOpen && !categoriesState) {
      dispatch(getCategories());
    }
  }, [isOpen, dispatch, categoriesState]);

  useEffect(() => {
    // Only clear state when modal is closed and we have data
    if (!isOpen && categoriesState) {
      clearReduxState(setGetCategories);
    }
  }, [isOpen, categoriesState]);

  const handleCategoryToggle = (categoryName) => {
    setSelectedCats((prev) => {
      if (prev.includes(categoryName)) {
        return prev.filter((cat) => cat !== categoryName);
      } else {
        return [...prev, categoryName];
      }
    });
  };

  const handleSubcategoryToggle = (categoryName, subcategoryName) => {
    const fullName = `${categoryName} - ${subcategoryName}`;
    setSelectedCats((prev) => {
      if (prev.includes(fullName)) {
        return prev.filter((cat) => cat !== fullName);
      } else {
        return [...prev, fullName];
      }
    });
  };

  const handleConfirm = () => {
    onConfirm(selectedCats, includeClientsWithoutInterests);
    onClose();
  };

  const handleSelectAll = () => {
    if (!categoriesState?.data?.data) return;

    const allCategories = [];
    categoriesState.data.data.forEach((category) => {
      allCategories.push(category.category_name);
      category.types.forEach((type) => {
        allCategories.push(`${category.category_name} - ${type.name}`);
      });
    });
    setSelectedCats(allCategories);
  };

  const handleSelectNone = () => {
    setSelectedCats([]);
  };

  const filteredCategories =
    categoriesState?.data?.data?.filter(
      (category) =>
        category.category_name
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        category.types.some((type) =>
          type.name.toLowerCase().includes(searchTerm.toLowerCase()),
        ),
    ) || [];

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" fade={false}>
      <ModalHeader toggle={onClose}>
        Seleccionar Categorías para Descarga
      </ModalHeader>
      <ModalBody>
        <div className="search-container mb-3">
          <Input
            type="text"
            placeholder="Buscar categorías..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="selection-actions mb-3">
          <Button
            size="sm"
            color="primary"
            outline
            className="mr-2"
            onClick={handleSelectAll}
          >
            Seleccionar Todas
          </Button>
          <Button
            size="sm"
            color="secondary"
            outline
            onClick={handleSelectNone}
          >
            Deseleccionar Todas
          </Button>
        </div>

        <div
          className="include-no-interests mb-3 p-2"
          style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}
        >
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              margin: 0,
            }}
          >
            <input
              type="checkbox"
              checked={includeClientsWithoutInterests}
              onChange={(e) =>
                setIncludeClientsWithoutInterests(e.target.checked)
              }
              style={{
                marginRight: '0.5rem',
                width: '1rem',
                height: '1rem',
                cursor: 'pointer',
              }}
            />
            <span style={{ fontWeight: 500, color: '#495057' }}>
              Incluir clientes sin intereses
            </span>
          </label>
          <small
            className="text-muted d-block mt-1"
            style={{ fontSize: '0.875rem', marginLeft: '1.5rem' }}
          >
            Se incluirán clientes que no tienen categorías de interés
            seleccionadas
          </small>
        </div>

        <div className="categories-container">
          {filteredCategories.map((category) => (
            <div key={category.category_value} className="category-group">
              <div className="category-item">
                <label className="category-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedCats.includes(category.category_name)}
                    onChange={() =>
                      handleCategoryToggle(category.category_name)
                    }
                  />
                  <span className="category-name">
                    {category.category_name}
                  </span>
                </label>
              </div>

              {category.types && category.types.length > 0 && (
                <div className="subcategories">
                  {category.types.map((type, index) => (
                    <div key={index} className="subcategory-item">
                      <label className="subcategory-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedCats.includes(
                            `${category.category_name} - ${type.name}`,
                          )}
                          onChange={() =>
                            handleSubcategoryToggle(
                              category.category_name,
                              type.name,
                            )
                          }
                        />
                        <span className="subcategory-name">{type.name}</span>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {selectedCats.length > 0 && (
          <div className="selected-summary mt-3">
            <strong>Categorías seleccionadas ({selectedCats.length}):</strong>
            <div className="selected-tags">
              {selectedCats.map((cat, index) => (
                <span key={index} className="selected-tag">
                  {cat}
                </span>
              ))}
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          color="primary"
          onClick={handleConfirm}
          disabled={
            selectedCats.length === 0 && !includeClientsWithoutInterests
          }
        >
          Descargar CSV
          {selectedCats.length > 0 && ` (${selectedCats.length} categorías)`}
          {includeClientsWithoutInterests && ' + Sin intereses'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CategorySelectionModal;
